// The skater's state types: one per thing the skater can be doing. The mode
// modules in this folder step them; `Skater` (../skater.ts) owns them.

import type { HillHitKind } from "../hill-course";
import type { HillScore } from "../hill-score";
import type { RideId } from "./rides";
import type { Rail } from "../level";
import type { ItemId, PaintHit, WashHit } from "./backpack";
import type {
  Dir,
  Dir8,
  FlipTrick,
  FlipVariant,
  GrindTrick,
  LipTrick,
  ManualHoldTrick,
  ManualPopTrick,
  ManualTrick,
  PopVariantId,
} from "../tricks";

export type Mode = "ground" | "air" | "grind" | "lip" | "bail" | "foot";

export type Landing = "sloppy" | "perfect";

export type SkaterEvent =
  /** `hill`: on a hill (#205), what the combo added to the run's trick score. */
  | { type: "landed"; points: number; combo: string; landing?: Landing; hill?: number }
  /** `wipeout`: into the sea (#82). */
  | { type: "bail"; wipeout?: true }
  | { type: "respawn" }
  /** A gap went into the combo (see gaps.ts). */
  | { type: "gap"; name: string; points: number }
  /** The backpack (#136): a different item came out, on foot. */
  | { type: "item"; item: ItemId }
  /** The paintball gun fired. */
  | { type: "shot" }
  /** A paintball hit the park and left a splat there. */
  | { type: "splat"; hit: PaintHit }
  /** A paintball burst one of the map's targets (#137): which, and how many are down of how many. */
  | { type: "target"; index: number; burst: number; of: number }
  /** The power washer's jet (#151) hit the park here this step (one a step while it sprays). */
  | { type: "wash"; hit: WashHit }
  /** The jet washed one of the map's graffiti tags clean (#151): which, and how many are clean of how many. */
  | { type: "clean"; index: number; cleaned: number; of: number }
  /** The jet knocked over one of the map's props (#151): which (index into `Level.props`). */
  | { type: "knock"; index: number }
  /** A run down the hill (#193) ended: how, and how far down it got (m). */
  | { type: "hill"; end: HillEnd; distance: number; hit?: HillHit; score: HillScore }
  /** The hill's road moved back up under the rider (#195): everything at z is now at z + dz, dy higher. */
  | { type: "hillShift"; dz: number; dy: number }
  /** A close call on the hill (#196): someone on a crosswalk passed this close, at (x, z), for `bonus` metres. */
  | { type: "hillClose"; x: number; z: number; bonus: number }
  /** A side street's car (#197) honking: it's coming for the rider's line. */
  | { type: "hillHorn" }
  /** A close shave on the hill (#197): whipped past a side street's car this close, at (x, z), for `bonus` metres. */
  | { type: "hillShave"; x: number; z: number; bonus: number };

/** What a run down the hill ended on (#194): something in the road, someone crossing it (#196), or (#195) the wall beside it. */
export type HillHit = HillHitKind | "wall";

/**
 * How a run down the hill ended (#193): a face plant from speed wobbles,
 * the wheels sliding out of a hard carve, any other bail (hitting something),
 * running into something in the road (#194, `hit`: which kind it was),
 * or reaching the bottom of a fixed hill.
 */
export type HillEnd = "plant" | "slide" | "crash" | "hit" | "bottom";

/** Bomb the Hill (#193): one run down the hill, kept on `Skater.hill` on a hill map. */
export interface HillState {
  /** Where the run started (z): the distance is measured down from here. */
  startZ: number;
  /** The furthest down the hill this run has got (m). */
  distance: number;
  /** Metres added to the distance for close calls with people on crosswalks (#196), and how many. */
  bonus: number;
  closeCalls: number;
  /** Where the rider was last step (z), so a crosswalk's people are counted as they go by. */
  lastZ: number;
  /** The carve, -1 (hard left) .. 1 (hard right), easing toward the stick. */
  carve: number;
  /** Speed wobbles, 0 .. 1: at 1 you face-plant. */
  wobble: number;
  /** The wheels sliding out of a hard carve this step. */
  sliding: boolean;
  /** How close the slide is to a fall, 0 .. 1. */
  slip: number;
  /** How the run ended, or null while it's on. */
  end: HillEnd | null;
  /** What you ran into, when the run ended on one (#194). */
  hit: HillHit | null;
  /** On a gravel patch this step (#194): no grip, so the carve neither turns nor brakes. */
  gravel: boolean;
  /** Seconds since it ended; at HILL_RESET you're back at the top. */
  endT: number;
  /**
   * Still at the top picking a ride (#195): left and right on the stick
   * go through `HILL_RIDES`, ollie (or Start on a pad, #207) drops in. The ride's locked from
   * then until the run ends.
   */
  picking: boolean;
  /** Which way the stick's push that stepped the ride went (-1, 1), 0 once it's back near the middle: one push is one step. */
  pickDir: number;
  /** The ride picked for this run. */
  ride: RideId;
  /** This run's road seed (#195): the same seed lays out the same road. */
  seed: number;
  /** The last side street (#197) whose traffic honked at the rider, in course z (park z plus the road's offset), so each honks once. */
  honked: number;
  /** Speed points banked this run (#205, `hill-score.ts`). */
  speedPts: number;
  /** Speed points from the wobble zone, banked once you're back under wobble speed, lost if the run ends first. */
  wobblePts: number;
  /** Trick points this run, each combo weighted by how far down and how fast it landed. */
  trickPts: number;
  /** Seconds the open combo has spent barely moving (a camped combo pays nothing). */
  slowT: number;
}

export interface FlipState {
  /** The single flip the first tap picked. */
  base: FlipTrick;
  /** What it's become: the single, or its Double / Triple. */
  trick: FlipVariant;
  dir: Dir8;
  rotations: number;
  /** Combo entry, renamed and repriced when a tap upgrades the flip. */
  index: number;
  t: number;
  /** Seconds since the last tap; another tap in time adds a rotation. */
  sinceTap: number;
  /** Seconds to finish the flip; longer when riding switch. */
  duration: number;
  /** A special flip (Kickflip Underflip), or a whip during a body flip: it never upgrades the board's way. */
  special: boolean;
  /** A whip during a body flip (#138): it shares the flip's entry, and a quick tap doubles it once (Backflip Double Tailwhip). */
  inFlip: boolean;
}

export interface GrabState {
  name: string;
  index: number;
  perSecond: number;
  t: number;
  /** A special grab (Christ Air): a big pose you have to come back from before landing. */
  special: boolean;
  /** Seconds since a special was let go, while getting back on the board; null while held. */
  released: number | null;
  /** A spin special (McTwist, 900), or a bike's body flip: it runs by itself and ends at `duration`, not when let go. */
  timed: {
    duration: number;
    spinRate: number;
    invert: boolean;
    /** Whole flips head over heels (the bike's Backflip, #138): > 0 backward, < 0 forward. */
    flips: number;
    hand: string | null;
    /** A whip has been done during this flip (Backflip Tailwhip, #138): one a flip. */
    whipped: boolean;
    /** The grab taken during this flip (Superman Backflip, #138), or null: one a flip, and not with a whip. */
    grabbed: string | null;
  } | null;
}

/** Balancing on two wheels (THPS 2): `balance` > 0 tips toward the nose. */
export interface ManualState {
  trick: ManualTrick;
  index: number;
  balance: number;
  balanceVel: number;
  time: number;
  /** A flatground flip or shove-it in progress; lands back in the manual. */
  pop: {
    trick: ManualPopTrick;
    index: number;
    t: number;
    duration: number;
  } | null;
  /** A held flatground trick (Casper, Pogo...), scoring while the grab button is held. */
  hold: { trick: ManualHoldTrick; index: number; t: number } | null;
}

/** Stalled on the coping at the top of a quarter pipe (THPS lip trick). */
export interface LipState {
  trick: LipTrick;
  index: number;
  balance: number;
  balanceVel: number;
  time: number;
  /** The quarter's up-ramp axis, and its radius (for the drop back in). */
  axisX: number;
  axisZ: number;
  r: number;
  /** Grind was held when the stall started, so letting go of it drops you in. */
  held: boolean;
}

/** Riding along a wall in the air (THPS wallride): a combo entry scoring over time. */
export interface WallrideState {
  /** Which way the wall faces. */
  n: { x: number; z: number };
  index: number;
  t: number;
}

/** Towed behind a vehicle (#37): a left / right needle, and a combo entry scoring over time. */
export interface SkitchState {
  /** Index into the level's vehicles. */
  vehicle: number;
  index: number;
  balance: number;
  balanceVel: number;
  time: number;
  /** Riding fakie behind it. */
  fakie: boolean;
}

/** Off the board and on foot (#19), board carried. */
export interface FootState {
  /** Pace along the way you face (m/s); negative backs up. */
  speed: number;
  /** Up speed in a jump or a fall. */
  vy: number;
  airborne: boolean;
  /** Steps taken (a foot comes down at each half, #122), for the walk cycle and the footsteps. */
  steps: number;
  /** Seconds left of a combo's run out (0 when you stepped off outside one): it banks when this runs down. */
  runOut: number;
  /** Hanging off a ledge or climbing up it, or null. */
  ledge: LedgeState | null;
}

/**
 * Caught a ledge on foot (#19): hanging by the hands below its top, then
 * climbing up and over onto it.
 */
export interface LedgeState {
  /** The ledge's top (m). */
  top: number;
  /** Where you stand once you're up: a little in from the edge. */
  x: number;
  z: number;
  /** Where you hang (feet at `y0`, settling there after the catch), and where the climb starts from. */
  x0: number;
  y0: number;
  z0: number;
  /** -1 while hanging, then 0..1 through the climb. */
  climb: number;
}

/** The step-off chord's first trigger (#19): which, how long ago, and nollie and stance before it. */
export interface ChordState {
  first: "nollie" | "revert";
  t: number;
  nollie: boolean;
  switchStance: boolean;
}

export interface GrindState {
  rail: Rail;
  t: number;
  dir: 1 | -1;
  speed: number;
  trick: GrindTrick;
  index: number;
  balance: number;
  balanceVel: number;
  time: number;
  /** An ollie pressed just before locking on (#244): it pops out once the grind has lasted `GRIND_POP_HOLD`. */
  popQueued: boolean;
}

/** Airborne against a wall: hanging (grace to plant) or planted (sticking, then kicking off). */
export interface WallState {
  n: { x: number; z: number };
  t: number;
  impact: number;
  planted: boolean;
  alongX: number;
  alongZ: number;
}

/**
 * Everything that lives for one air, from leaving the ground (or a rail) to
 * landing, bailing or locking onto a rail. Created by `enterAir` and dropped
 * on the way out, so none of it needs resetting by hand. Wallrides and wall
 * hangs are still the same air.
 */
export interface AirState {
  /** Where this air started, for air and grind gaps; cleared once they're scored. */
  takeoff: { x: number; z: number } | null;
  /** Player-driven rotation so far, credited to the air's first trick. */
  spin: number;
  /** Combo index of this air's first trick (the one a spin or pop names), or -1. */
  firstIndex: number;
  /** Popped off the nose. */
  nollie: boolean;
  /** Popped while rolling fakie (and not off the nose). */
  fakie: boolean;
  /** Boneless / Fastplant this air was popped with, for the pose; and seconds since the pop. */
  popVariant: PopVariantId | null;
  popT: number;
  /** Set on a vert launch (or an acid drop / spine transfer): the ramp's up axis, to judge the landing against. */
  vertAxis: { x: number; z: number } | null;
  /** How far (rad) the vert assist has turned the board this air (#183): it stops at VERT_ALIGN_MAX, and a Perfect has to be earned without it. */
  vertAssist: number;
  /**
   * Off a bowl's corner (#11), how far (rad) the board was off the line when
   * the sim turned the carve into a straight up-and-down air: not the
   * rider's doing, so the assist gives it back on top of its own limit.
   */
  vertCarve: number;
  /** Set on a vert launch: the wall's normal, which the board keeps for the whole air (THPS). */
  vertUp: { x: number; y: number; z: number } | null;
  /** Set on a vert launch off a spine: where the shared coping is and what's on the far side. */
  spineLip: {
    cx: number;
    cz: number;
    cy: number;
    r: number;
    axisX: number;
    axisZ: number;
  } | null;
  /** Still crouched at a vert launch: seconds left to let go for the ollie, and what it's worked out from. */
  vertOllieLate: { t: number; vy0: number; cap: number; charge: number; fakie: boolean } | null;
  /**
   * Up held at a vert launch (#77): the speed `vh` to carry out along the
   * ramp's axis once the air is up past `clearY` (just over the coping).
   */
  transferOut: { vh: number; axisX: number; axisZ: number; clearY: number } | null;
  /**
   * A transfer air out over the deck (#77): the up-ramp axis it left along,
   * and `ramp` once it's coming down a far ramp (one facing back the other way).
   */
  transfer: { axisX: number; axisZ: number; ramp: boolean } | null;
  /**
   * A side transfer (#116): where it launched, which way along the coping
   * it's heading (unit dx, dz) and how far along that the far ramp starts.
   */
  side: { x: number; z: number; dx: number; dz: number; edge: number } | null;
  /**
   * A grab pressed while a flip is spinning (#84): it starts the moment the
   * flip finishes. `t` is how long it keeps once grab is let go, `seq` the
   * special sequence it was pressed after.
   */
  grabBuffer: { t: number; seq: [Dir, Dir] | null } | null;
  /** Set by an acid drop: the air's speed going in, which the landing keeps at least. */
  acidCarry: number;
  /** An R2 tapped on the ground just before takeoff, still live: tried as a transfer or acid drop (#134). */
  earlyRevert: number;
  /** A vert air's ground speed at the lip, which a coping caught in it carries (#133); 0 for any other air. */
  lipSpeed: number;
  flip: FlipState | null;
  grab: GrabState | null;
  wall: WallState | null;
  /** Riding along a wall; still this air. */
  wallride: WallrideState | null;
  /**
   * A side hop off a grind (#232): which way it went (unit sx, sz), so no rail
   * back on that side locks on this air, and the shoulder that did it, which
   * doesn't spin you until it's let go (null once it is).
   */
  hop: { sx: number; sz: number; button: "spinL" | "spinR" | null } | null;
  /** Seconds an ollie pressed in this air stays live for the rail it's about to lock onto (#244), 0 if none. */
  popPress: number;
}

export function newAir(x: number, z: number): AirState {
  return {
    takeoff: { x, z },
    spin: 0,
    firstIndex: -1,
    nollie: false,
    fakie: false,
    popVariant: null,
    popT: 0,
    vertAxis: null,
    vertAssist: 0,
    vertCarve: 0,
    vertUp: null,
    spineLip: null,
    vertOllieLate: null,
    transferOut: null,
    transfer: null,
    side: null,
    grabBuffer: null,
    acidCarry: 0,
    earlyRevert: 0,
    lipSpeed: 0,
    flip: null,
    grab: null,
    wall: null,
    wallride: null,
    hop: null,
    popPress: 0,
  };
}
